import { useState, useContext } from "react";
import {
  Typography,
  Box,
  Grid,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  TableContainer,
  Button,
} from "@mui/material";
// Import from project
import "../../../Style/pagestyle.scss";
import { AuthContext } from "../../../Context/AuthContext";
import { ButtonCreate, SearchTextField } from "../../../Utils/Component";
import EmptyImage from "../../../Assets/empty-image.png";
import EmptyData from "../../../Components/Include/EmptyData/EmptyData";
import LoadingPage from "../../../Utils/LoadingPage";
import FooterPagination from "../../../Utils/FooterPagination";
import useWithDimension from "../../../Hooks/useWithDimension";
import LessonForm from "../../../Components/Lesson/LessonForm";
import LessonAction from "../../../Components/Lesson/LessonAction";
import useGetLessonPagination from "../../../Hooks/useGetLessonPagination";

export default function Lesson({ classId }) {
  const { t } = useContext(AuthContext);
  const { width } = useWithDimension();

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const {
    page,
    limit,
    setPage,
    refetch,
    loading,
    setLoading,
    handleSearch,
    tableData,
    paginationData,
    handleLimit,
  } = useGetLessonPagination({ classId: classId });

  // console.log("tableData::", tableData);

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  return (
    <div className="page-container">
      <Box sx={{ marginTop: "10px" }}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6} md={4} lg={3} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("search")}
            </Typography>
            <SearchTextField
              className={"text-field"}
              handleSearch={handleSearch}
            />
          </Grid>
          {/* <Grid item xs={6} sm={6} md={4} lg={3} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("page_content")}
            </Typography>
            <SelectContent
              value={contentIdVal}
              onChange={(val) => setContentIdVal(val)}
              className="text-field"
            />
          </Grid> */}
          <Grid item xs={6} sm={6} md={8} lg={9} xl={10}>
            <ButtonCreate
              handleOpen={handleOpen}
              classBtn={"btn-create"}
              classIcon={"icon-add"}
            />
            {open && (
              <LessonForm
                open={open}
                handleClose={handleClose}
                dialogTitle="Create"
                setRefetch={refetch}
                classId={classId}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      <Box className="body-container">
        <TableContainer sx={{ maxWidth: `${width}px` }}>
          <Table className="table">
            <TableHead className="header-row">
              <TableRow>
                <TableCell
                  className={t("class-name-header-title-start")}
                  width={"5%"}
                >
                  {t("thead_no")}
                </TableCell>
                {/* <TableCell
                  className={t("class-name-header-title")}
                  width={"10%"}
                >
                  {t("page_content")}
                </TableCell> */}
                <TableCell
                  className={t("class-name-header-title")}
                  width={"15%"}
                >
                  {t("thead_title")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title")}
                  width={"10%"}
                >
                  {t("thead_profile")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title")}
                  width={"17%"}
                >
                  {t("thead_group_fee")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title")}
                  width={"25%"}
                >
                  {t("thead_video_link")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title")}
                  width={"25%"}
                >
                  {t("thead_description")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title-end")}
                  width={"10%"}
                  align="right"
                ></TableCell>
              </TableRow>
            </TableHead>

            {loading ? (
              <TableBody className="body">
                <TableRow className="body-row">
                  <TableCell colSpan={10} className="body-cell" align="center">
                    <LoadingPage />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody className="body">
                {!tableData || tableData.length === 0 ? (
                  <TableRow className="body-row">
                    <TableCell
                      colSpan={10}
                      className="body-cell"
                      align="center"
                    >
                      <EmptyData />
                    </TableCell>
                  </TableRow>
                ) : (
                  tableData.map((row, index) => {
                    const isExpanded = expandedRows[row?._id] || false;

                    return (
                      <TableRow className="body-row" key={row?._id || index}>
                        <TableCell className="body-cell-start">
                          {row?.numbering || "-- : --"}
                        </TableCell>

                        <TableCell className="body-cell">
                          {row?.title}
                        </TableCell>

                        <TableCell className="body-cell">
                          <img
                            alt="lesson-preview"
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "4px",
                              objectFit: "cover",
                            }}
                            src={row?.image || EmptyImage}
                          />
                        </TableCell>

                        <TableCell className="body-cell">
                          {row?.groupFeeId?.title} : {row?.groupFeeId?.price} $
                        </TableCell>

                        <TableCell className="body-cell">
                          {row?.videoLink}
                        </TableCell>

                        <TableCell className="body-cell">
                          {isExpanded
                            ? row?.description
                            : row?.description?.slice(0, 50)}
                          {row?.description?.length > 50 && (
                            <Button
                              onClick={() => toggleRowExpansion(row?._id)}
                              size="small"
                              className="see-more"
                            >
                              {isExpanded ? "See less" : "...See more"}
                            </Button>
                          )}
                        </TableCell>

                        <TableCell className="body-cell-end" align="right">
                          <LessonAction setRefetch={refetch} editData={row} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <FooterPagination
          totalPages={paginationData?.totalPages}
          totalDocs={paginationData?.totalDocs}
          limit={limit}
          page={page}
          setPage={setPage}
          handleLimit={handleLimit}
          setLoading={setLoading}
        />
      </Box>
    </div>
  );
}
